/**
 * 倪師原話瀏覽／搜尋（CC BY-NC-SA 4.0 · 自用／非商業）
 */
export type NihaiEntry = {
  i: number;
  lecture: string;
  lectureTitle: string;
  quote: string;
  t: string;
  tSec: number;
  bvid: string;
  part: number;
  h2: string;
  h3: string;
  book: string;
  bookLabel: string;
  note: string;
  url: string;
};

export type NihaiLecture = { id: string; title: string; part: number; count: number };

export type NihaiCorpus = {
  meta: {
    license: string;
    licenseUrl: string;
    count: number;
    nonCommercial: boolean;
    attribution: { name: string; url: string; path?: string }[];
    copyrightNote: string;
    shareAlike: string;
  };
  lectures: NihaiLecture[];
  entries: NihaiEntry[];
};

const PAGE_SIZE = 40;

let corpus: NihaiCorpus | null = null;
let loadError = '';
let filterLecture = '';
let filterBook = '';
let keyword = '';
let page = 0;

export function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function highlight(text: string, kw: string): string {
  const safe = escapeHtml(text);
  const q = kw.trim();
  if (!q) return safe;
  const parts = q.split(/\s+/).filter(Boolean).map((p) => p.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  if (!parts.length) return safe;
  try {
    const re = new RegExp(`(${parts.join('|')})`, 'gi');
    return safe.replace(re, '<mark>$1</mark>');
  } catch {
    return safe;
  }
}

export async function loadNihaiCorpus(): Promise<NihaiCorpus> {
  if (corpus) return corpus;
  // Vite copies public/ → dist/; base is './' so relative path works on Pages
  const candidates = ['./data/nihai-quotes.json', 'data/nihai-quotes.json'];
  let lastErr: unknown;
  for (const c of candidates) {
    try {
      const res = await fetch(c);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      corpus = (await res.json()) as NihaiCorpus;
      loadError = '';
      return corpus;
    } catch (e) {
      lastErr = e;
    }
  }
  loadError = lastErr instanceof Error ? lastErr.message : String(lastErr);
  throw lastErr instanceof Error ? lastErr : new Error(String(lastErr));
}

function filtered(): NihaiEntry[] {
  if (!corpus) return [];
  const kw = keyword.trim().toLowerCase();
  return corpus.entries.filter((e) => {
    if (filterLecture && e.lecture !== filterLecture) return false;
    if (filterBook && e.book !== filterBook) return false;
    if (!kw) return true;
    const hay = `${e.quote}\n${e.h2}\n${e.h3}\n${e.note}\n${e.lecture}\n${e.bookLabel}`.toLowerCase();
    return kw.split(/\s+/).every((t) => hay.includes(t));
  });
}

export function ncBannerHtml(): string {
  return `
    <div class="nc-banner" role="status">
      <strong>本站倪師原話僅供個人非商業使用</strong>
      · 授權 <a href="https://creativecommons.org/licenses/by-nc-sa/4.0/deed.zh-hans" target="_blank" rel="noopener">CC BY-NC-SA 4.0</a>
      · 來源
      <a href="https://github.com/Renhuai123/ziwei-doushu" target="_blank" rel="noopener">ziwei-doushu</a>
      ／
      <a href="https://github.com/Renhuai123/nihai-tianji-corpus" target="_blank" rel="noopener">nihai-tianji-corpus</a>
      · <em>不可商用</em> · 改編須相同方式共享
    </div>`;
}

export function renderNihaiPanel(_onChange?: () => void): string {
  if (loadError && !corpus) {
    return `
      ${ncBannerHtml()}
      <section class="panel">
        <h2>倪師原話</h2>
        <p class="err">載入失敗：${escapeHtml(loadError)}</p>
        <button type="button" id="nihai-retry">重試</button>
      </section>`;
  }
  if (!corpus) {
    return `
      ${ncBannerHtml()}
      <section class="panel stub">正在載入倪師原話語料…</section>`;
  }

  const list = filtered();
  const totalPages = Math.max(1, Math.ceil(list.length / PAGE_SIZE));
  if (page >= totalPages) page = totalPages - 1;
  if (page < 0) page = 0;
  const slice = list.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);

  const lectureOpts = corpus.lectures
    .map(
      (l) =>
        `<option value="${escapeHtml(l.id)}" ${filterLecture === l.id ? 'selected' : ''}>${escapeHtml(l.id)} · ${escapeHtml(l.title)}（${l.count}）</option>`,
    )
    .join('');

  const books = Array.from(new Map(corpus.entries.map((e) => [e.book, e.bookLabel])).entries()).sort(
    (a, b) => a[0].localeCompare(b[0]),
  );
  const bookOpts = books
    .map(
      ([id, label]) =>
        `<option value="${escapeHtml(id)}" ${filterBook === id ? 'selected' : ''}>${escapeHtml(label)}（${id}）</option>`,
    )
    .join('');

  const cards = slice
    .map((e) => {
      const tags = [e.bookLabel, e.h2, e.h3].filter(Boolean).map((t) => `<span class="tag">${escapeHtml(t)}</span>`).join('');
      const note = e.note ? `<div class="quote-note">${highlight(e.note, keyword)}</div>` : '';
      return `
        <article class="quote-card">
          <div class="quote-meta">
            <span class="lec">${escapeHtml(e.lecture)}</span>
            <a class="ts" href="${escapeHtml(e.url)}" target="_blank" rel="noopener" title="Bilibili 原片">${escapeHtml(e.t)} ↗</a>
          </div>
          <blockquote>「${highlight(e.quote, keyword)}」</blockquote>
          <div class="tags">${tags}</div>
          ${note}
        </article>`;
    })
    .join('');

  return `
    ${ncBannerHtml()}
    <section class="panel">
      <h2>倪師原話（${corpus.meta.count} 條）</h2>
      <p class="note">短引文整理自倪海廈《天紀·紫微斗數》講次；撳時間戳可喺新分頁開 B 站原片對應秒數。自用／非商業；唔構成任何預測或建議。</p>
      <div class="controls nihai-controls">
        <label>講次
          <select id="nihai-lecture">
            <option value="">全部講次</option>
            ${lectureOpts}
          </select>
        </label>
        <label>冊目
          <select id="nihai-book">
            <option value="">全部冊目</option>
            ${bookOpts}
          </select>
        </label>
        <label class="grow">關鍵字
          <input id="nihai-kw" type="search" placeholder="搜原話／主題…" value="${escapeHtml(keyword)}" />
        </label>
      </div>
      <p class="note">顯示 ${list.length} 條 · 第 ${page + 1}／${totalPages} 頁</p>
      <div class="quote-list">${cards || '<p class="stub">冇符合條件嘅原話。</p>'}</div>
      <div class="btn-row nihai-pager">
        <button type="button" class="secondary" id="nihai-prev" ${page <= 0 ? 'disabled' : ''}>上一頁</button>
        <button type="button" class="secondary" id="nihai-next" ${page >= totalPages - 1 ? 'disabled' : ''}>下一頁</button>
      </div>
    </section>
    <section class="panel nc-detail">
      <h2>授權說明</h2>
      <ul class="note-list">
        <li><strong>自用／非商業</strong>：本頁原話<strong>不可商用</strong>。</li>
        <li>協議：<a href="${escapeHtml(corpus.meta.licenseUrl)}" target="_blank" rel="noopener">${escapeHtml(corpus.meta.license)}</a>（署名—非商業性使用—相同方式共享）。</li>
        <li>來源：${corpus.meta.attribution.map((a) => `<a href="${escapeHtml(a.url)}" target="_blank" rel="noopener">${escapeHtml(a.name)}</a>`).join(' · ')}</li>
        <li>${escapeHtml(corpus.meta.copyrightNote)}</li>
        <li>${escapeHtml(corpus.meta.shareAlike)}</li>
        <li>本站排盤引擎仍係 MIT；原話語料同引擎授權分開。</li>
      </ul>
    </section>`;
}

export function bindNihaiPanel(root: HTMLElement, onChange: () => void): void {
  root.querySelector('#nihai-retry')?.addEventListener('click', () => {
    corpus = null;
    loadError = '';
    void loadNihaiCorpus()
      .then(() => onChange())
      .catch(() => onChange());
  });

  const lec = root.querySelector<HTMLSelectElement>('#nihai-lecture');
  const book = root.querySelector<HTMLSelectElement>('#nihai-book');
  const kw = root.querySelector<HTMLInputElement>('#nihai-kw');
  lec?.addEventListener('change', () => {
    filterLecture = lec.value;
    page = 0;
    onChange();
  });
  book?.addEventListener('change', () => {
    filterBook = book.value;
    page = 0;
    onChange();
  });
  let timer: ReturnType<typeof setTimeout> | undefined;
  kw?.addEventListener('input', () => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      keyword = kw.value;
      page = 0;
      onChange();
    }, 180);
  });
  root.querySelector('#nihai-prev')?.addEventListener('click', () => {
    page = Math.max(0, page - 1);
    onChange();
  });
  root.querySelector('#nihai-next')?.addEventListener('click', () => {
    page += 1;
    onChange();
  });
}

export function getNihaiLoadError(): string {
  return loadError;
}

export function hasNihaiCorpus(): boolean {
  return !!corpus;
}

/** 畀題問導航跳去原話分頁時預填關鍵字 */
export function setNihaiKeyword(kw: string): void {
  keyword = kw;
  page = 0;
}

export function getNihaiKeyword(): string {
  return keyword;
}

export function getNihaiEntries(): NihaiEntry[] {
  return corpus?.entries ?? [];
}
